import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { sendTestError, IS_SENTRY_ENABLED } from '@/app/utils/errorTracking';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface HelpDialogProps {
  open: boolean;
  onClose: () => void;
}

export function HelpDialog({ open, onClose }: HelpDialogProps) {
  const { t } = useLanguage();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('help.title')}</DialogTitle>
          <DialogDescription>
            {t('help.description')}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="tools" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="tools">{t('help.tabs.tools')}</TabsTrigger>
            <TabsTrigger value="shortcuts">{t('help.tabs.shortcuts')}</TabsTrigger>
            <TabsTrigger value="tips">{t('help.tabs.tips')}</TabsTrigger>
          </TabsList>

          <TabsContent value="tools" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('editor.tools.select')} (V)</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                {t('help.toolDescriptions.select')}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('editor.tools.line')} (L)</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                {t('help.toolDescriptions.line')}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('editor.tools.shape')} (P)</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                {t('help.toolDescriptions.shape')}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('editor.tools.text')} (T)</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                {t('help.toolDescriptions.text')}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('editor.tools.pan')} (H)</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                {t('help.toolDescriptions.pan')}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="shortcuts" className="space-y-2">
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">{t('help.tabs.tools')}</CardTitle>
                </CardHeader>
                <CardContent className="text-xs space-y-1">
                  <div className="flex justify-between">
                    <span>{t('editor.tools.select')}:</span>
                    <kbd className="px-2 py-0.5 bg-gray-100 rounded">V</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('editor.tools.line')}:</span>
                    <kbd className="px-2 py-0.5 bg-gray-100 rounded">L</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('editor.tools.shape')}:</span>
                    <kbd className="px-2 py-0.5 bg-gray-100 rounded">P</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('editor.tools.text')}:</span>
                    <kbd className="px-2 py-0.5 bg-gray-100 rounded">T</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('editor.tools.pan')}:</span>
                    <kbd className="px-2 py-0.5 bg-gray-100 rounded">H</kbd>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">{t('help.sections.view')}</CardTitle>
                </CardHeader>
                <CardContent className="text-xs space-y-1">
                  <div className="flex justify-between">
                    <span>{t('editor.view.zoomIn')}:</span>
                    <kbd className="px-2 py-0.5 bg-gray-100 rounded">+</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('editor.view.zoomOut')}:</span>
                    <kbd className="px-2 py-0.5 bg-gray-100 rounded">-</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('editor.view.zoomReset')}:</span>
                    <kbd className="px-2 py-0.5 bg-gray-100 rounded">0</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('editor.view.toggleGrid')}:</span>
                    <kbd className="px-2 py-0.5 bg-gray-100 rounded">G</kbd>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">{t('help.sections.file')}</CardTitle>
                </CardHeader>
                <CardContent className="text-xs space-y-1">
                  <div className="flex justify-between">
                    <span>{t('common.newPart')}:</span>
                    <kbd className="px-2 py-0.5 bg-gray-100 rounded">Strg+N</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('common.save')}:</span>
                    <kbd className="px-2 py-0.5 bg-gray-100 rounded">Strg+S</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('common.load')}:</span>
                    <kbd className="px-2 py-0.5 bg-gray-100 rounded">Strg+O</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('common.symbolLibrary')}:</span>
                    <kbd className="px-2 py-0.5 bg-gray-100 rounded">Strg+B</kbd>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">{t('help.sections.edit')}</CardTitle>
                </CardHeader>
                <CardContent className="text-xs space-y-1">
                  <div className="flex justify-between">
                    <span>{t('editor.actions.undo')}:</span>
                    <kbd className="px-2 py-0.5 bg-gray-100 rounded">Strg+Z</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('editor.actions.redo')}:</span>
                    <kbd className="px-2 py-0.5 bg-gray-100 rounded">Strg+Y</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('common.delete')}:</span>
                    <kbd className="px-2 py-0.5 bg-gray-100 rounded">Entf</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('common.cancel')}:</span>
                    <kbd className="px-2 py-0.5 bg-gray-100 rounded">ESC</kbd>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="tips" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('help.tips.grid.title')}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <p>
                  {t('help.tips.grid.desc')}
                </p>
                <p className="text-xs text-gray-600">
                  {t('help.tips.grid.sub')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('help.tips.shapes.title')}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <p>
                  {t('help.tips.shapes.desc')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('help.tips.nav.title')}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <p>
                  {t('help.tips.nav.zoom')}
                </p>
                <p>
                  {t('help.tips.nav.pan')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('help.tips.props.title')}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <p>
                  {t('help.tips.props.desc')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('help.tips.save.title')}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <p>
                  {t('help.tips.save.desc')}
                </p>
              </CardContent>
            </Card>

            {/* Sentry Test Section - Only visible when Sentry is enabled */}
            {IS_SENTRY_ENABLED && (
              <Card className="border-orange-200 bg-orange-50">
                <CardHeader>
                  <CardTitle className="text-base text-orange-900">{t('sentry.test.title')}</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-3">
                  <p className="text-orange-800">
                    {t('sentry.test.desc')}
                  </p>
                  <Button
                    onClick={() => {
                      sendTestError();
                    }}
                    variant="outline"
                    size="sm"
                    className="w-full border-orange-300 bg-white hover:bg-orange-100 text-orange-900"
                  >
                    {t('sentry.test.button')}
                  </Button>
                  <p className="text-xs text-orange-700">
                    {t('sentry.test.note')}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}